import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { CAUSE_NAMES } from "@/lib/causes";
import { CheckCircle2, XCircle } from "lucide-react";

export default function OrganizerQueue() {
  const [data, setData] = useState({ opportunities: [], pending_verifications: [] });
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/me/organized").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const decide = async (signup, approve) => {
    setBusy(signup.id + (approve ? "-y" : "-n"));
    setErr("");
    try {
      await api.post("/opportunities/verify", {
        signup_id: signup.id,
        approve,
        note: approve ? "Verified by organizer" : "Could not verify",
      });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <Layout>
      <div data-testid="organize-page" className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <span className="overline text-[var(--cm-primary)]">Organize</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">Your opportunities.</h1>
        <p className="text-ink-soft mt-2 max-w-xl">Verify volunteer hours to credit them. Without your approval, no hours are awarded.</p>

        {err && <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">{err}</div>}

        <section className="mt-10">
          <h2 className="font-display font-bold text-2xl mb-4 tracking-tight">Awaiting verification</h2>
          {data.pending_verifications.length === 0 ? (
            <div className="card-soft p-8 text-ink-soft" data-testid="organize-no-pending">
              You're all caught up — no volunteers waiting on you right now.
            </div>
          ) : (
            <div className="space-y-3">
              {data.pending_verifications.map((s) => (
                <div key={s.id} className="card-soft p-6 flex flex-wrap items-center gap-4 justify-between" data-testid={`organize-pending-${s.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <h3 className="font-display font-semibold">{s.opportunity_title}</h3>
                    <p className="text-xs text-ink-soft mt-1">
                      Volunteer: <span className="font-semibold text-ink">{s.user_name}</span> · @{s.user_username}
                    </p>
                    <p className="text-xs text-ink-soft mt-1">
                      Cause: {CAUSE_NAMES[s.opportunity_cause]}
                    </p>
                  </div>
                  <TimeBadge hours={s.opportunity_hours} />
                  <div className="flex gap-2">
                    <button
                      data-testid={`organize-approve-${s.id}`}
                      onClick={() => decide(s, true)}
                      disabled={busy.startsWith(s.id)}
                      className="btn-primary inline-flex items-center gap-1.5 text-sm py-1.5 px-4 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                    <button
                      data-testid={`organize-reject-${s.id}`}
                      onClick={() => decide(s, false)}
                      disabled={busy.startsWith(s.id)}
                      className="btn-outline inline-flex items-center gap-1.5 text-sm py-1.5 px-4 disabled:opacity-50"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-14">
          <h2 className="font-display font-bold text-2xl mb-4 tracking-tight">Posted by you</h2>
          {data.opportunities.length === 0 ? (
            <div className="card-soft p-8 text-ink-soft" data-testid="organize-no-opps">
              You haven't posted any opportunities yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {data.opportunities.map((o) => (
                <div key={o.id} className="card-soft p-6" data-testid={`organize-opp-${o.id}`}>
                  <h3 className="font-display font-semibold">{o.title}</h3>
                  <p className="text-xs text-ink-soft mt-1">
                    {CAUSE_NAMES[o.cause]} · {o.mode === "online" ? "Online" : o.location} · {o.is_ongoing ? "Ongoing" : (o.date || "No date set")}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <TimeBadge hours={o.hours} size="sm" />
                    <span className="text-xs text-ink-soft">{o.signups_count || 0} signed up</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
